import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Upload, X, Save } from 'lucide-react';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';

export async function uploadFile(kind, file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(`/admin/uploads/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return { url: data.url, original_filename: data.original_filename || null };
}

export function FileField({ label, kind, value, onChange, accept }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { url, original_filename } = await uploadFile(kind, f);
      // Second argument carries upload metadata (e.g. original_filename) for
      // callers that need it (e.g. digital product download field). Callers
      // that only care about the URL can safely ignore it.
      onChange(url, { original_filename });
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };
  return (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      <div className="mt-1 flex gap-2 items-center">
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="URL or upload…" className="flex-1 pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm" />
        <input ref={inputRef} type="file" onChange={onFile} accept={accept} className="hidden" />
        <button data-testid={ADMIN.uploadButton} type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2">
          <Upload size={14} /> {uploading ? '…' : 'Upload'}
        </button>
      </div>
      {value && <p className="mt-1 text-[11px] text-[var(--pg-ink-faint)] font-mono break-all">{value}</p>}
    </div>
  );
}

export function TextField({ label, value, onChange, type = 'text', textarea, rows = 3, placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      {textarea ? (
        <textarea
          rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm leading-relaxed"
        />
      ) : (
        <input
          type={type} value={value ?? ''} onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)} placeholder={placeholder}
          className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}

export function Toggle({ label, value, onChange }) {
  return (
    <label className="flex items-center justify-between gap-2 px-3 py-2 pg-input rounded-[var(--pg-radius-sm)] cursor-pointer">
      <span className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</span>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[var(--pg-emerald)]" />
    </label>
  );
}

export function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
        {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ===== Publishing status: shared components =====
export const PUBLISH_STATES = [
  { value: 'draft',     label: 'Draft',     hint: 'Only visible to admins' },
  { value: 'published', label: 'Published', hint: 'Live on the public site' },
  { value: 'hidden',    label: 'Hidden',    hint: 'Removed from public view' },
  { value: 'archived',  label: 'Archived',  hint: 'Retired but preserved for restoration' },
];

const STATUS_STYLE = {
  draft:     { ring: 'var(--pg-ink-faint)', bg: 'rgba(150,140,120,0.14)', dot: '#c2b8a3', text: 'var(--pg-ink)' },
  published: { ring: 'var(--pg-emerald)',   bg: 'rgba(25,194,142,0.14)',  dot: 'var(--pg-emerald)', text: 'var(--pg-emerald-2)' },
  hidden:    { ring: 'var(--pg-gold-2)',    bg: 'rgba(226,199,122,0.14)', dot: 'var(--pg-gold-2)',  text: 'var(--pg-gold-2)' },
  archived:  { ring: 'var(--pg-neon-pink)', bg: 'rgba(255,62,165,0.12)',  dot: 'var(--pg-neon-pink)', text: 'var(--pg-neon-pink)' },
};

export function PublishStatusBadge({ status = 'published', size = 'sm' }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.published;
  const cls = size === 'xs'
    ? 'text-[9px] px-1.5 py-0.5'
    : 'text-[10px] px-2 py-0.5';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-mono tracking-[0.24em] uppercase ${cls}`}
      style={{ background: s.bg, color: s.text, border: `1px solid ${s.ring}` }}
      data-testid={`publish-status-badge-${status}`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} />
      {status}
    </span>
  );
}

export function PublishStatusControl({ value = 'published', onChange, label = 'Publishing state', compact = false }) {
  return (
    <div data-testid="publish-status-control">
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      <div className={`mt-1 grid grid-cols-2 ${compact ? 'gap-1.5' : 'gap-2'}`}>
        {PUBLISH_STATES.map((s) => {
          const active = value === s.value;
          const style = STATUS_STYLE[s.value];
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => onChange(s.value)}
              title={s.hint}
              data-testid={`publish-status-option-${s.value}`}
              className={`text-left px-3 py-2 rounded-[var(--pg-radius-sm)] border transition-colors text-xs font-mono tracking-[0.24em] uppercase`}
              style={{
                background: active ? style.bg : 'transparent',
                borderColor: active ? style.ring : 'var(--pg-border-2)',
                color: active ? style.text : 'var(--pg-ink-muted)',
              }}
            >
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: style.dot }} />
                {s.label}
              </span>
              {!compact && <span className="block mt-1 text-[9px] tracking-normal normal-case text-[var(--pg-ink-faint)]">{s.hint}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PublishStatusTabs({ value = 'all', onChange, counts = {} }) {
  const tabs = [
    { key: 'all',       label: 'All' },
    { key: 'published', label: 'Published' },
    { key: 'draft',     label: 'Drafts' },
    { key: 'hidden',    label: 'Hidden' },
    { key: 'archived',  label: 'Archived' },
  ];
  return (
    <div className="flex items-center flex-wrap gap-1.5 mb-3" data-testid="publish-status-tabs">
      {tabs.map((t) => {
        const active = value === t.key;
        const style = t.key === 'all' ? { ring: 'var(--pg-ink-muted)', bg: 'rgba(255,255,255,0.06)', text: 'var(--pg-ink)' } : STATUS_STYLE[t.key];
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            data-testid={`publish-status-tab-${t.key}`}
            className="px-3 py-1.5 rounded-full border text-[10px] font-mono tracking-[0.24em] uppercase transition-colors"
            style={{
              background: active ? style.bg : 'transparent',
              borderColor: active ? style.ring : 'var(--pg-border-2)',
              color: active ? style.text : 'var(--pg-ink-muted)',
            }}
          >
            {t.label}
            {typeof counts[t.key] === 'number' && (
              <span className="ml-1.5 text-[9px] text-[var(--pg-ink-faint)]">{counts[t.key]}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Drawer({ open, onClose, title, children, onSave, saving }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
          <div className="absolute inset-0 bg-[rgba(7,11,20,0.7)] backdrop-blur-sm" />
          <motion.div
            initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 50, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16,1,0.3,1] }}
            className="relative w-full max-w-xl h-full bg-[var(--pg-navy-900)] border-l border-[var(--pg-border-2)] overflow-y-auto pg-scroll"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[var(--pg-border-2)] sticky top-0 bg-[var(--pg-navy-900)] flex items-center justify-between z-10">
              <h3 className="font-serif text-xl text-[var(--pg-ink)]">{title}</h3>
              <button onClick={onClose} aria-label="close" className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">{children}</div>
            <div className="p-5 border-t border-[var(--pg-border-2)] sticky bottom-0 bg-[var(--pg-navy-900)]">
              <button data-testid={ADMIN.saveButton} onClick={onSave} disabled={saving} className="w-full pg-btn-primary rounded-[var(--pg-radius-md)] py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
